import { useMemo, useState } from "react";
import { Link } from "react-router";
import { PillView, SortTh, Toolbar, useSorted } from "../../components/ui";
import { money } from "../../lib/format";
import { useAgenturen, useDemoMarks } from "./hooks";
import { DemoGebucht } from "./DemoTermine";
import { MailPill, useMailStats } from "./Betriebsdaten";
import {
  countLabel, limitsOf, listKpis, matchesList, monthlyCents, ORG_STATUS, orgStatusPill, priceLabel, subscriptionLabel, trialDaysLeft, trialLabel,
  TRIAL_FILTERS, utcDate, type ListFilter, type OrgOverviewRow, type StatusFilter, type TrialFilter,
} from "./model";
import { DemoHinweis, Fehler, Laden } from "./parts";

/** Agenturen (17_betreiber/agenturen.html): Kennzahlen, Suche, Filter nach Status, Tarif und Ende der Testphase. */

const ALL: ListFilter = { search: "", status: "alle", plan: "alle", trial: "alle" };
const limitText = (v: number | undefined) => (v === undefined ? "–" : v < 0 ? "∞" : countLabel(v));

export function Agenturen() {
  const q = useAgenturen();
  const mails = useMailStats();
  const mailOf = (id: string) => mails.data?.find((m) => m.org_id === id);
  // 0390: Agenturen, deren Admin eine Demo gebucht hat
  const demos = useDemoMarks();
  const demoOf = (id: string) => demos.data?.find((m) => m.org_id === id);
  const [nowMs] = useState(() => Date.now());
  const [filter, setFilter] = useState<ListFilter>(ALL);
  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const shown = useMemo(() => rows.filter((r) => matchesList(r, filter, nowMs)), [rows, filter, nowMs]);
  const planOrder = useMemo(() => new Map((q.data?.plans ?? []).map((p) => [p.key, p.sort_order])), [q.data]);
  const { sorted, sort, toggle } = useSorted(shown, {
    name: (r: OrgOverviewRow) => r.name,
    tarif: (r: OrgOverviewRow) => (r.plan_key ? planOrder.get(r.plan_key) ?? 99 : null),
    creator: (r: OrgOverviewRow) => r.talents,
    abrechnung: (r: OrgOverviewRow) => monthlyCents(r),
    status: (r: OrgOverviewRow) => orgStatusPill(r.status).label,
    testphase: (r: OrgOverviewRow) => trialDaysLeft(r, nowMs),
    vertraege: (r: OrgOverviewRow) => r.contracts_this_month,
    aktivitaet: (r: OrgOverviewRow) => r.last_activity_at,
  });

  if (q.error) return <><title>Agenturen · CreatorOffice</title><h1>Agenturen</h1><Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending || !q.data) return <><title>Agenturen · CreatorOffice</title><h1>Agenturen</h1><Laden /></>;

  const { plans, entitlements } = q.data;
  const k = listKpis(rows, nowMs);
  const statusFilters: { key: StatusFilter; label: string; count: number }[] = [
    { key: "alle", label: "Alle", count: rows.length },
    ...ORG_STATUS.map((s) => ({ key: s.key as StatusFilter, label: s.label, count: rows.filter((r) => r.status === s.key).length })),
  ];
  const filtered = filter.search.trim() !== "" || filter.status !== "alle" || filter.plan !== "alle" || filter.trial !== "alle";

  return (
    <>
      <title>Agenturen · CreatorOffice</title>
      <h1>Agenturen</h1>
      <div className="grid three">
        <section className="panel stack">
          <h2>Aktive Agenturen</h2>
          <div className="metric">{countLabel(k.active)}</div>
          <p>von {countLabel(k.total)} {k.total === 1 ? "Agentur" : "Agenturen"} insgesamt</p>
        </section>
        <section className="panel stack">
          <h2>MRR · netto</h2>
          <div className="metric num">{money(k.mrrCents)}</div>
          <p>{k.paying === 1 ? "1 zahlendes Abo" : `${countLabel(k.paying)} zahlende Abos`} · Jahresabos anteilig</p>
        </section>
        <section className="panel stack">
          <h2>Testphasen</h2>
          <div className="metric">{countLabel(k.trials)}</div>
          <p>{k.trialsSoon === 0 ? "Keine endet in den nächsten 7 Tagen" : `${countLabel(k.trialsSoon)} ${k.trialsSoon === 1 ? "endet" : "enden"} in den nächsten 7 Tagen`}</p>
        </section>
      </div>

      <Toolbar
        search={filter.search}
        onSearch={(search) => setFilter((f) => ({ ...f, search }))}
        label="Agenturen durchsuchen"
        placeholder="Agentur, Kürzel oder Admin …"
        filters={statusFilters}
        filter={filter.status}
        onFilter={(status) => setFilter((f) => ({ ...f, status }))}
      >
        <select aria-label="Tarif" value={filter.plan} onChange={(e) => setFilter((f) => ({ ...f, plan: e.target.value }))}>
          <option value="alle">Alle Tarife</option>
          {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          {rows.some((r) => !r.plan_key) && <option value="ohne">Ohne Tarif</option>}
        </select>
        <select aria-label="Ende der Testphase" value={filter.trial} onChange={(e) => setFilter((f) => ({ ...f, trial: e.target.value as TrialFilter }))}>
          {TRIAL_FILTERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {filtered && <button className="" onClick={() => setFilter(ALL)}>Filter zurücksetzen</button>}
      </Toolbar>

      <div className="tablewrap">
        <table>
          <caption className="sr-only">Agenturen der Plattform</caption>
          <thead>
            <tr>
              <SortTh label="Agentur" k="name" sort={sort} toggle={toggle} />
              <SortTh label="Tarif" k="tarif" sort={sort} toggle={toggle} />
              <SortTh label="Creator / Team" k="creator" sort={sort} toggle={toggle} />
              <SortTh label="Abrechnung" k="abrechnung" sort={sort} toggle={toggle} money />
              <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
              <SortTh label="Testphase endet" k="testphase" sort={sort} toggle={toggle} />
              <SortTh label="Verträge im Monat" k="vertraege" sort={sort} toggle={toggle} money />
              <SortTh label="Letzte Aktivität" k="aktivitaet" sort={sort} toggle={toggle} />
              <th scope="col">Mails · 30 Tage</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const limits = limitsOf(r.plan_key, entitlements);
              const days = trialDaysLeft(r, nowMs);
              return (
                <tr key={r.org_id}>
                  <td>
                    <Link to={`/betrieb/agenturen/${r.org_id}`}><strong>{r.name}</strong></Link>
                    <small>{r.slug}</small>
                    <DemoGebucht mark={demoOf(r.org_id)} />
                  </td>
                  <td>
                    {r.plan_name ?? "Ohne Tarif"}
                    {r.subscription_status && r.subscription_status !== "active" && <small>Abo: {subscriptionLabel(r.subscription_status)}</small>}
                  </td>
                  <td>
                    <span className="num">{countLabel(r.talents)} / {countLabel(r.seats)}</span>
                    <small>Grenze {limitText(limits.max_talents)} / {limitText(limits.max_seats)}</small>
                  </td>
                  <td><span className="num">{priceLabel(r)}</span></td>
                  <td><PillView pill={orgStatusPill(r.status)} /></td>
                  <td>
                    {days === null ? "–" : (
                      <>
                        {utcDate(r.trial_ends_at)}
                        <small>{trialLabel(days)}</small>
                      </>
                    )}
                  </td>
                  <td>
                    <span className="num">{countLabel(r.contracts_this_month)}</span>
                    <small>von {limitText(limits.max_contracts_per_month)}</small>
                  </td>
                  <td>{r.last_activity_at ? utcDate(r.last_activity_at) : "–"}</td>
                  <td><MailPill s={mailOf(r.org_id)} /></td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={9}>
                  {rows.length ? "Keine passenden Ergebnisse. Passe die Suche oder die Filter an." : "Noch keine Agentur auf der Plattform."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="source-note">
        {sorted.length === 1 ? "1 Agentur" : `${countLabel(sorted.length)} Agenturen`} · Creator ohne archivierte, Team sind aktive Plätze, Verträge zählen den Versand
        im laufenden Monat (UTC) wie die Tarifgrenze. MRR netto: aktive Abos und Abos mit offener Zahlung zum Preis des Tarifkatalogs, Jahresabos anteilig.
        Letzte Aktivität: letzte Änderung durch Team oder Creator oder letzter Besuch des Teams; Systemläufe und Support zählen nicht.
        Inhalte der Agenturen (Creator, Marken, Rechnungen) sieht die Konsole nicht.
      </p>
      <DemoHinweis />
    </>
  );
}
